import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Film, Sparkles, Timer, Cpu, Gamepad2, Lightbulb } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { UploadZone } from "@/components/UploadZone";
import { VideoCard } from "@/components/VideoCard";
import { LogoFull } from "@/components/Logo";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="cx-card rounded-xl p-4 sm:p-5 cx-rise" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <span className="cx-label truncate" style={{ color: accent }}>{label}</span>
      <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
    </div>
    <div className="font-display text-2xl sm:text-3xl font-bold mt-3 text-white">{value}</div>
  </div>
);

const SidePanel = ({ stats, t }) => (
  <div className="grid sm:grid-cols-2 xl:grid-cols-1 gap-6">
    <div className="cx-card rounded-2xl p-6" data-testid="render-queue-panel">
      <div className="cx-label flex items-center gap-2"><Cpu className="w-3.5 h-3.5" /> {t("dash.queue")}</div>
      <div className="font-display text-5xl font-bold text-white mt-4" data-testid="render-queue-count">{stats?.rendering ?? 0}</div>
      <div className="text-sm text-slate-400 mt-1">{t("dash.queue_sub")}</div>
      <div className="mt-6 text-xs text-slate-500 leading-relaxed">{t("dash.queue_note", { h: stats?.hours ?? 0 })}</div>
    </div>
    <div className="cx-card rounded-2xl p-6" data-testid="ai-tips-panel">
      <div className="cx-label flex items-center gap-2" style={{ color: "#c79bff" }}><Lightbulb className="w-3.5 h-3.5" /> {t("dash.tips")}</div>
      <ul className="mt-4 space-y-3 text-sm text-slate-300">
        {["dash.tip1", "dash.tip2", "dash.tip3"].map((k, i) => (
          <li key={k} className="flex gap-3"><span className="font-mono text-[#3ad7ff] text-xs mt-0.5">0{i + 1}</span><span>{t(k)}</span></li>
        ))}
      </ul>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const [v, s] = await Promise.all([api.get("/videos"), api.get("/stats")]);
    setVideos(v.data);
    setStats(s.data);
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  const [params, setParams] = useSearchParams();
  const importedRef = useRef(null);
  const { lang } = useI18n();
  useEffect(() => {
    const url = params.get("import");
    if (!url || importedRef.current === url) return;
    importedRef.current = url;
    setParams({}, { replace: true });
    api.post("/videos/import", { url, analyze: { lang } }).then(() => { toast.success(t("up.import_started")); load(); }).catch((e) => !e.cxLimit && toast.error(formatApiError(e)));
  }, [params, setParams, lang, t, load]);

  const busy = videos.some((v) => ["uploading", "processing", "analyzing"].includes(v.status)) || stats?.rendering > 0;
  useEffect(() => {
    if (!busy) return;
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [busy, load]);

  const remove = async (v) => {
    if (!window.confirm(t("dash.confirm_delete", { t: v.title }))) return;
    try {
      await api.delete(`/videos/${v.id}`);
      toast.success(t("dash.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 2xl:px-14 py-6 sm:py-10">
      <header className="flex items-center justify-between gap-6 cx-rise">
        <div className="min-w-0">
          <div className="cx-label">{t("dash.label")} · {user?.workspace?.name}</div>
          <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl mt-3 leading-tight"><span className="cx-chrome">{t("dash.hello")}</span><span className="cx-text-grad">{user?.name}</span></h1>
          <p className="text-slate-400 mt-3 max-w-2xl text-sm sm:text-base">{t("dash.sub")}</p>
        </div>
        <LogoFull className="hidden md:grid w-48 h-32 lg:w-64 lg:h-44 shrink-0" testid="dashboard-logo" />
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
        <Stat testid="stat-videos" icon={Gamepad2} label={t("dash.videos")} value={stats?.videos ?? "–"} accent="#3ad7ff" />
        <Stat testid="stat-highlights" icon={Sparkles} label={t("dash.highlights")} value={stats?.highlights ?? "–"} accent="#c79bff" />
        <Stat testid="stat-clips" icon={Film} label={t("dash.clips")} value={stats?.clips ?? "–"} accent="#2f7bff" />
        <Stat testid="stat-time-saved" icon={Timer} label={t("dash.saved")} value={stats ? `${Math.round(stats.time_saved_min / 6) / 10}h` : "–"} accent="#22e3a1" />
      </section>

      <section className="grid xl:grid-cols-[1fr_380px] 2xl:grid-cols-[1fr_440px] gap-6 mt-6">
        <UploadZone onUploaded={load} />
        <SidePanel stats={stats} t={t} />
      </section>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-white">{t("dash.recent")}</h2>
          <span className="font-mono text-xs text-slate-500">{t("dash.count", { n: videos.length })}</span>
        </div>
        {loaded && videos.length === 0 ? (
          <div className="cx-card rounded-xl p-10 sm:p-12 text-center text-slate-400" data-testid="videos-empty">{t("dash.empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[1900px]:grid-cols-5 gap-5" data-testid="videos-grid">
            {videos.map((v, i) => <VideoCard key={v.id} v={v} index={i} onDelete={remove} />)}
          </div>
        )}
      </section>
    </div>
  );
}
